(() => {
  const initDragScroll = (scroller) => {
    if (!scroller || scroller.dataset.cceDragReady === 'true') return;
    scroller.dataset.cceDragReady = 'true';

    let dragging = false;
    let startX = 0;
    let startScrollLeft = 0;
    let moved = false;

    scroller.addEventListener('pointerdown', (event) => {
      /* Real phones use native touch scrolling. This adds mouse/pen dragging
         for Theme Editor and desktop mobile previews without hijacking touch.

         Pointer capture is deferred until an actual drag is confirmed
         (movement > 4px): capturing on pointerdown retargets the subsequent
         click to the scroller itself (per the pointer-events spec), so plain
         clicks never reach the <a> cards and links silently die. */
      if (event.pointerType === 'touch' || event.button !== 0) return;

      dragging = true;
      moved = false;
      startX = event.clientX;
      startScrollLeft = scroller.scrollLeft;
    });

    scroller.addEventListener('pointermove', (event) => {
      if (!dragging) return;
      const distance = event.clientX - startX;
      if (!moved && Math.abs(distance) > 4) {
        moved = true;
        scroller.setPointerCapture?.(event.pointerId);
        scroller.classList.add('is-dragging');
      }
      if (!moved) return;
      scroller.scrollLeft = startScrollLeft - distance;
      event.preventDefault();
    });

    const stopDragging = (event) => {
      if (!dragging) return;
      dragging = false;
      scroller.releasePointerCapture?.(event.pointerId);
      scroller.classList.remove('is-dragging');
      /* The click that belongs to a finished drag is dispatched synchronously
         after pointerup, so the capture-phase click guard below still eats it;
         this then clears the flag so the NEXT genuine click is never
         swallowed (previously `moved` could leak when no click followed). */
      window.setTimeout(() => { moved = false; }, 0);
    };

    scroller.addEventListener('pointerup', stopDragging);
    scroller.addEventListener('pointercancel', stopDragging);
    scroller.addEventListener('lostpointercapture', () => {
      dragging = false;
      scroller.classList.remove('is-dragging');
    });

    scroller.addEventListener('click', (event) => {
      if (!moved) return;
      event.preventDefault();
      event.stopPropagation();
      moved = false;
    }, true);
  };

  const initTabs = (root) => {
    if (!root || root.dataset.cceTabsReady === 'true') return;

    const tabs = Array.from(root.querySelectorAll('[data-cce-tab]'));
    const panels = Array.from(root.querySelectorAll('[data-cce-panel]'));
    if (!tabs.length || !panels.length) return;

    root.dataset.cceTabsReady = 'true';

    const activate = (tab, focus = false) => {
      tabs.forEach((item) => {
        const active = item === tab;
        item.setAttribute('aria-selected', active ? 'true' : 'false');
        item.tabIndex = active ? 0 : -1;
      });

      panels.forEach((panel) => {
        panel.hidden = panel.dataset.ccePanel !== tab.dataset.cceTab;
      });

      const activePanel = panels.find((panel) => !panel.hidden);
      if (activePanel) {
        const grid = activePanel.querySelector('.cce__finder-grid');
        if (grid) {
          grid.scrollLeft = 0;
          initDragScroll(grid);
        }
      }

      if (focus) tab.focus();
    };

    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => activate(tab));
      tab.addEventListener('keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();

        let nextIndex = index;
        if (event.key === 'ArrowRight') nextIndex = (index + 1) % tabs.length;
        if (event.key === 'ArrowLeft') nextIndex = (index - 1 + tabs.length) % tabs.length;
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = tabs.length - 1;

        activate(tabs[nextIndex], true);
      });
    });

    root.querySelectorAll('.cce__finder-grid').forEach(initDragScroll);
  };

  const initAll = (scope = document) => {
    scope.querySelectorAll('[data-cce-tabs]').forEach(initTabs);
    scope.querySelectorAll('.cce__finder-grid').forEach(initDragScroll);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();

/* =========================================================
   Gate 3 final ruling — unified arrows for ALL horizontal
   card rows on the editorial collection pages.

   Rule: every horizontally scrolling card module gets the
   same prev/next arrows as the terrain slider (same markup,
   class, full-card stepping, edge-disabled states). The only
   exemption is the comparison table (.cce__table-scroll).

   Implementation notes:
   - A document-level capture handler drives ALL is=previous/
     next-button clicks deterministically: it does not depend
     on the theme's SliderElement init (Motion.inView) and
     falls back to an instant scroll on engines that cancel
     smooth scrolling inside scroll-snap containers.
   - An injection engine scans for horizontal card rows that
     lack visible arrows on mobile and appends a navigation
     block identical to the terrain slider's.
   - Desktop (>760px) is untouched: injected navs reuse the
     .cce__terrain-navigation class, which is display:none
     outside the mobile media query.
========================================================= */
(() => {
  const MOBILE_MQ = window.matchMedia('screen and (max-width: 760px)');
  const EXEMPT_SELECTOR = '.cce__table-scroll';

  const stepFor = (scroller) => {
    let step = 0;
    try {
      if (typeof scroller.itemOffset === 'number' && scroller.itemOffset > 0) {
        const perPage = typeof scroller.perPage === 'number' && scroller.perPage > 0 ? scroller.perPage : 1;
        step = scroller.itemOffset * perPage;
      }
    } catch (e) { /* uninitialized slider getters may throw */ }
    if (!step) {
      const kids = scroller.children;
      if (kids.length > 1) {
        const delta = Math.round(kids[1].getBoundingClientRect().left - kids[0].getBoundingClientRect().left);
        if (delta > 40) step = delta;
      }
    }
    if (!step || !isFinite(step)) step = Math.max(Math.round(scroller.clientWidth * 0.85), 120);
    return step;
  };

  const buttonsFor = (scroller, dir) => {
    if (!scroller.id) return [];
    const is = dir < 0 ? 'previous-button' : 'next-button';
    return Array.from(document.querySelectorAll('button[is=' + is + '][aria-controls="' + scroller.id.replace(/"/g, '\\"') + '"]'));
  };

  const syncArrows = (scroller) => {
    const truncated = scroller.scrollWidth > scroller.clientWidth + 2;
    const maxLeft = scroller.scrollWidth - scroller.clientWidth;
    buttonsFor(scroller, -1).forEach((b) => { b.disabled = !truncated || scroller.scrollLeft <= 2; });
    buttonsFor(scroller, 1).forEach((b) => { b.disabled = !truncated || scroller.scrollLeft >= maxLeft - 2; });
    const nav = scroller.nextElementSibling;
    if (nav && nav.classList.contains('cce__row-navigation')) {
      nav.hidden = !truncated;
    }
  };

  const watched = new WeakSet();
  const rows = new Set();
  const watchScroller = (scroller) => {
    rows.add(scroller);
    if (watched.has(scroller)) return;
    watched.add(scroller);
    let t;
    scroller.addEventListener('scroll', () => {
      clearTimeout(t);
      t = setTimeout(() => syncArrows(scroller), 120);
    }, { passive: true });
  };

  document.addEventListener('click', (event) => {
    const btn = event.target.closest && event.target.closest('button[is=previous-button], button[is=next-button]');
    if (!btn || btn.disabled) return;
    const id = btn.getAttribute('aria-controls');
    const scroller = id ? document.getElementById(id) : null;
    if (!scroller) return;
    if (scroller.scrollWidth <= scroller.clientWidth + 2) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    const dir = btn.getAttribute('is') === 'next-button' ? 1 : -1;
    // Rapid clicks accumulate from the pending target, not the live scroll
    // position, so no step is ever swallowed mid-animation.
    const pending = parseFloat(scroller.dataset.cceTarget);
    const base = isFinite(pending) ? pending : scroller.scrollLeft;
    const target = Math.max(0, Math.min(base + dir * stepFor(scroller), scroller.scrollWidth - scroller.clientWidth));
    scroller.dataset.cceTarget = String(target);
    const before = scroller.scrollLeft;
    scroller.scrollTo({ left: target, behavior: 'smooth' });
    window.setTimeout(() => {
      if (parseFloat(scroller.dataset.cceTarget) === target && Math.abs(scroller.scrollLeft - target) >= 2) {
        // 'instant', not 'auto': containers styled with CSS scroll-behavior:
        // smooth (e.g. the theme's .slider--tablet) would otherwise re-enter
        // the very smooth-scroll path this fallback exists to bypass.
        scroller.scrollTo({ left: target, behavior: 'instant' });
      }
    }, 300);
    window.setTimeout(() => {
      if (parseFloat(scroller.dataset.cceTarget) === target) delete scroller.dataset.cceTarget;
    }, 900);

    watchScroller(scroller);
    window.setTimeout(() => syncArrows(scroller), 450);
  }, true);

  /* ---------- arrow injection engine ---------- */

  /* Same classes as the theme's icon snippet output — .icon/.icon-m carry the
     size (22px) and stroke-width; without them the SVG stretches to fill the
     button and renders an invisible hairline on real devices. */
  const ICON_LEFT = '<svg class="icon icon-chevron-left icon-m" viewBox="0 0 24 24" stroke="currentColor" fill="none" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" d="M14 6L8 12L14 18"></path></svg>';
  const ICON_RIGHT = '<svg class="icon icon-chevron-right icon-m" viewBox="0 0 24 24" stroke="currentColor" fill="none" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" d="M10 6L16 12L10 18"></path></svg>';

  let uid = 0;
  const injected = new WeakSet();

  const makeButton = (dir, controlsId) => {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'button button--secondary';
    b.setAttribute('is', dir < 0 ? 'previous-button' : 'next-button');
    b.setAttribute('aria-controls', controlsId);
    b.setAttribute('aria-label', dir < 0 ? 'Previous' : 'Next');
    if (dir < 0) b.disabled = true;
    b.innerHTML = '<span class="btn-fill" data-fill></span><span class="btn-text">' + (dir < 0 ? ICON_LEFT : ICON_RIGHT) + '</span>';
    return b;
  };

  const hasVisibleArrows = (scroller) => {
    return buttonsFor(scroller, 1).concat(buttonsFor(scroller, -1)).some((b) => {
      const r = b.getBoundingClientRect();
      return r.width > 0 && getComputedStyle(b).display !== 'none' && getComputedStyle(b).visibility !== 'hidden';
    });
  };

  const injectNav = (scroller) => {
    if (injected.has(scroller)) return;
    injected.add(scroller);
    if (!scroller.id) scroller.id = 'cce-row-' + (++uid);
    const nav = document.createElement('div');
    nav.className = 'cce__terrain-navigation cce__row-navigation';
    nav.setAttribute('aria-label', 'Card navigation');
    nav.appendChild(makeButton(-1, scroller.id));
    nav.appendChild(makeButton(1, scroller.id));
    scroller.insertAdjacentElement('afterend', nav);
    watchScroller(scroller);
    syncArrows(scroller);
  };

  const isCardRow = (el) => {
    if (el.children.length < 2) return false;
    if (el.closest(EXEMPT_SELECTOR) || el.matches(EXEMPT_SELECTOR)) return false;
    const cs = getComputedStyle(el);
    if (!['auto', 'scroll'].includes(cs.overflowX)) return false;
    return el.scrollWidth > el.clientWidth + 12 && el.clientWidth >= 200;
  };

  const scan = () => {
    if (!MOBILE_MQ.matches) return;
    const root = document.querySelector('main') || document;
    const candidates = new Set();
    // known card rows on the editorial pages (tab panels included even while hidden)
    root.querySelectorAll('.cce__finder-grid, .cce__review-grid, .cce__cert-grid, slider-element.grid').forEach((el) => candidates.add(el));
    // generic sweep: anything horizontally truncated that behaves like a card row
    root.querySelectorAll('*').forEach((el) => {
      if (candidates.has(el)) return;
      for (const c of candidates) { if (c.contains(el) || el.contains(c)) return; }
      if (el.closest('header, footer')) return;
      if (isCardRow(el)) candidates.add(el);
    });
    candidates.forEach((el) => {
      if (hasVisibleArrows(el)) { watchScroller(el); syncArrows(el); return; }
      injectNav(el);
    });
    rows.forEach(syncArrows);
  };

  const scheduleScan = () => { window.setTimeout(scan, 60); };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan);
  } else {
    scan();
  }
  window.addEventListener('load', scheduleScan);
  window.setTimeout(scan, 1500);
  window.setTimeout(scan, 4000);
  document.addEventListener('shopify:section:load', scheduleScan);
  MOBILE_MQ.addEventListener ? MOBILE_MQ.addEventListener('change', scheduleScan) : MOBILE_MQ.addListener(scheduleScan);
  let rt;
  window.addEventListener('resize', () => { clearTimeout(rt); rt = setTimeout(scan, 250); });
  // finder tab switches reveal a previously hidden panel grid
  document.addEventListener('click', (event) => {
    if (event.target.closest && event.target.closest('[data-cce-tab]')) scheduleScan();
  });
})();

/* cache-bust: click-fix rev2 2026-07-18 */
